/**
 * Analytics React Query Hooks
 *
 * Modern React Query hooks replacing legacy useState pattern
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query'
import {
  analyticsApi,
  AnalyticsParams,
  AnalyticsOverviewResponse,
  TimeseriesResponse,
  AgentUsageResponse,
  UserMetricsResponse,
  TransactionsResponse,
  MessagesResponse,
  ConversationsResponse,
} from '../api/analytics-api'

/**
 * Get analytics overview
 */
export function useAnalyticsOverview(
  params: AnalyticsParams & { enabled?: boolean } = {}
): UseQueryResult<AnalyticsOverviewResponse, Error> {
  const { enabled = true, ...apiParams } = params
  return useQuery({
    queryKey: ['analytics', 'overview', apiParams],
    queryFn: () => analyticsApi.getOverview(apiParams),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  })
}

/**
 * Get timeseries data for a metric
 */
export function useAnalyticsTimeseries(
  metric: string,
  params: AnalyticsParams & { enabled?: boolean } = {}
): UseQueryResult<TimeseriesResponse, Error> {
  const { enabled = true, ...apiParams } = params
  return useQuery({
    queryKey: ['analytics', 'timeseries', metric, apiParams],
    queryFn: () => analyticsApi.getTimeseries(metric, apiParams),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!metric && enabled,
  })
}

/**
 * Get agent usage analytics
 */
export function useAnalyticsAgents(
  params: AnalyticsParams & { enabled?: boolean } = {}
): UseQueryResult<AgentUsageResponse, Error> {
  const { enabled = true, ...apiParams } = params
  return useQuery({
    queryKey: ['analytics', 'agents', apiParams],
    queryFn: () => analyticsApi.getAgentUsage(apiParams),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  })
}

/**
 * Get user metrics
 */
export function useAnalyticsUsers(
  params: AnalyticsParams & { enabled?: boolean } = {}
): UseQueryResult<UserMetricsResponse, Error> {
  const { enabled = true, ...apiParams } = params
  return useQuery({
    queryKey: ['analytics', 'users', apiParams],
    queryFn: () => analyticsApi.getUserMetrics(apiParams),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  })
}

/**
 * Get transaction summary
 */
export function useAnalyticsTransactions(
  params: AnalyticsParams & { enabled?: boolean } = {}
): UseQueryResult<TransactionsResponse, Error> {
  const { enabled = true, ...apiParams } = params
  return useQuery({
    queryKey: ['analytics', 'transactions', apiParams],
    queryFn: () => analyticsApi.getTransactions(apiParams),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  })
}

/**
 * Get message analytics
 */
export function useAnalyticsMessages(
  params: AnalyticsParams & { enabled?: boolean } = {}
): UseQueryResult<MessagesResponse, Error> {
  const { enabled = true, ...apiParams } = params
  return useQuery({
    queryKey: ['analytics', 'messages', apiParams],
    queryFn: () => analyticsApi.getMessages(apiParams),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  })
}

/**
 * Get conversation analytics
 */
export function useAnalyticsConversations(
  params: AnalyticsParams & { enabled?: boolean } = {}
): UseQueryResult<ConversationsResponse, Error> {
  const { enabled = true, ...apiParams } = params
  return useQuery({
    queryKey: ['analytics', 'conversations', apiParams],
    queryFn: () => analyticsApi.getConversations(apiParams),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled,
  })
}
